import { Field, FieldArray, Formik } from 'formik'
import type { FieldProps, FormikProps } from 'formik'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import { useDebounceValue } from 'usehooks-ts'

import {
  DEFAULT_LOCALE,
  buildValidationRulesPayload,
  getContactTypeOptions,
  getNameTypeOptions,
  getQuestionTypeOptions,
  validationRulesYupSchema,
  TYPES_WITH_VALIDATION_RULES
} from '../questionFormOptions'
import { ValidationRulesSection } from '../validationRulesSection/ValidationRulesSection'
import { TYPES_WITH_CONDITIONALS } from '../../formsList/createFormModal/constants'

import { useMutation, useQueryList } from '@percent/utility'
import {
  AcknowledgeModal,
  ActionModal,
  AsyncSelect,
  Button,
  Flex,
  FormField,
  MarkdownTextArea,
  Modal,
  Select,
  TextInput
} from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { FormQuestionType } from '@percent/admin-dashboard/api/actions/grantProgram/grantProgram.types'
import type { AddQuestionFormValues, AddQuestionModalProps, QuestionOption } from './AddQuestionModal.types'

const generateUid = () => crypto.randomUUID()

const DECISION_OPTIONS: QuestionOption[] = [
  { uid: 'decision-pass', key: 'pass', label: '', conditionalQuestionIds: '' },
  { uid: 'decision-fail', key: 'fail', label: '', conditionalQuestionIds: '' }
]

const TYPES_WITH_OPTIONS: FormQuestionType[] = ['select', 'award_recommendation', 'decision']

export function AddQuestionModal({ isOpen, onClose, refresh }: Readonly<AddQuestionModalProps>) {
  const { t } = useTranslation()
  const { grantProgramService, adminService } = useServices()
  const questionTypeOptions = useMemo(() => getQuestionTypeOptions(t), [t])
  const contactTypeOptions = useMemo(() => getContactTypeOptions(t), [t])
  const nameTypeOptions = useMemo(() => getNameTypeOptions(t), [t])
  const [modalState, setModalState] = useState<'success' | 'error' | null>(null)
  const [partnerSearch, setPartnerSearch] = useState('')
  const [debouncedPartnerSearch] = useDebounceValue(partnerSearch, 500)

  const [{ data: partnerListData, isLoading: isPartnersLoading }] = useQueryList(
    ['/v1/admin/partners', debouncedPartnerSearch],
    adminService.queries.getPartnerList,
    { query: debouncedPartnerSearch, pageSize: 20 },
    { enabled: debouncedPartnerSearch.length >= 1 }
  )

  const partnerNameOptions = useMemo(
    () => partnerListData?.map(p => ({ label: p.name, value: p.id })) ?? [],
    [partnerListData]
  )

  const [{ isPending }, { apiFunc: createQuestion }] = useMutation(
    grantProgramService.createStandaloneFormQuestion,
    () => {
      refresh()
      setModalState('success')
    },
    () => setModalState('error')
  )

  const handleClose = useCallback(() => {
    onClose()
    setModalState(null)
    setPartnerSearch('')
  }, [onClose])

  const initialValues = useMemo(
    (): AddQuestionFormValues => ({
      key: '',
      label: '',
      displayName: '',
      type: 'text',
      placeholder: '',
      helpText: '',
      instructions: '',
      partnerId: '',
      required: false,
      hidden: false,
      autoTranslationsEnabled: false,
      multiple: false,
      options: [{ uid: generateUid(), key: '', label: '', conditionalQuestionIds: '' }],
      maxScore: '',
      scoreWeight: '',
      maxLength: '',
      addOrganisationQuestions: [{ uid: generateUid(), value: '' }],
      contactType: '',
      nameType: '',
      validationRules: []
    }),
    []
  )

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        key: Yup.string().min(1).required(t('errorMessage.required')),
        label: Yup.string().min(1).required(t('errorMessage.required')),
        type: Yup.string().required(t('errorMessage.required')),
        options: Yup.array().when('type', {
          is: (type: FormQuestionType) => TYPES_WITH_OPTIONS.includes(type),
          then: schema =>
            schema
              .of(
                Yup.object().shape({
                  key: Yup.string().min(1).required(t('errorMessage.required')),
                  label: Yup.string().min(1).required(t('errorMessage.required'))
                })
              )
              .min(1)
              .required()
        }),
        maxScore: Yup.number().when('type', {
          is: 'score',
          then: schema => schema.integer().min(1).max(10).required(t('errorMessage.required')),
          otherwise: schema => schema.optional()
        }),
        scoreWeight: Yup.number().when('type', {
          is: 'score',
          then: schema => schema.positive().required(t('errorMessage.required')),
          otherwise: schema => schema.optional()
        }),
        nameType: Yup.string().when('type', {
          is: 'name',
          then: schema => schema.required(t('errorMessage.required')),
          otherwise: schema => schema.optional()
        }),
        contactType: Yup.string().when('type', {
          is: 'name',
          then: schema => schema.required(t('errorMessage.required')),
          otherwise: schema => schema.optional()
        }),
        validationRules: validationRulesYupSchema(t)
      }),
    [t]
  )

  const handleSubmit = useCallback(
    (values: AddQuestionFormValues) => {
      const base = {
        key: values.key,
        label: { [DEFAULT_LOCALE]: values.label },
        type: values.type,
        ...(values.displayName ? { dashboardLabel: values.displayName } : {}),
        ...(values.placeholder ? { placeholder: { [DEFAULT_LOCALE]: values.placeholder } } : {}),
        ...(values.helpText ? { description: { [DEFAULT_LOCALE]: values.helpText } } : {}),
        ...(values.instructions ? { instructions: { [DEFAULT_LOCALE]: values.instructions } } : {}),
        ...(values.partnerId ? { partnerId: values.partnerId } : {}),
        required: values.required,
        hidden: values.hidden,
        autoTranslationsEnabled: values.autoTranslationsEnabled
      }

      let typeSpecific = {}

      if (TYPES_WITH_OPTIONS.includes(values.type)) {
        typeSpecific = {
          options: values.options.map(o => ({
            key: o.key,
            label: { [DEFAULT_LOCALE]: o.label },
            ...(o.conditionalQuestionIds.trim()
              ? {
                  conditionalQuestionIds: o.conditionalQuestionIds
                    .split(/[,\s]+/)
                    .map(id => id.trim())
                    .filter(Boolean)
                }
              : {})
          })),
          ...(values.type === 'select' ? { multiple: values.multiple } : {})
        }
      } else if (values.type === 'score') {
        typeSpecific = {
          maxScore: Number(values.maxScore),
          scoreWeight: Number(values.scoreWeight)
        }
      } else if (values.type === 'textarea') {
        typeSpecific = values.maxLength !== '' ? { maxLength: Number(values.maxLength) } : {}
      } else if (values.type === 'organisation_search') {
        typeSpecific = { addOrganisationQuestions: values.addOrganisationQuestions.map(q => q.value).filter(Boolean) }
      } else if (values.type === 'email') {
        typeSpecific = values.contactType ? { contactType: values.contactType } : {}
      } else if (values.type === 'name') {
        typeSpecific = { nameType: values.nameType, contactType: values.contactType }
      }

      createQuestion({
        ...base,
        ...typeSpecific,
        ...buildValidationRulesPayload(values.validationRules, values.type)
      })
    },
    [createQuestion]
  )

  const renderContent = () => {
    if (modalState === 'success') {
      return (
        <AcknowledgeModal
          result="positive"
          title={t('grants.forms.questions.addModal.success.title')}
          description={t('grants.forms.questions.addModal.success.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
        />
      )
    }

    if (modalState === 'error') {
      return (
        <AcknowledgeModal
          result="negative"
          title={t('grants.forms.questions.addModal.error.title')}
          description={t('grants.forms.questions.addModal.error.description')}
          buttonText={t('button.close')}
          handleClose={handleClose}
        />
      )
    }

    return (
      <Formik<AddQuestionFormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {(formik: FormikProps<AddQuestionFormValues>) => {
          const { type } = formik.values
          const isDecision = type === 'decision'
          const hasOptions = TYPES_WITH_OPTIONS.includes(type)

          return (
            <ActionModal
              title={t('grants.forms.questions.addModal.title')}
              primaryButtonText={t('grants.forms.questions.addModal.submit')}
              secondaryButtonText={t('button.cancel')}
              handleClose={handleClose}
              handleSubmit={formik.submitForm}
              loading={isPending}
              disabled={!formik.isValid || !formik.dirty || isPending}
              primaryBtnTestId="add-question-submit-button"
              secondaryBtnTestId="add-question-cancel-button"
              maxHeight="60vh"
            >
              <Flex direction="column" gap="lg">
                <Field name="label">
                  {({ field, meta }: FieldProps<string>) => (
                    <FormField
                      label={t('grants.forms.questions.addModal.label.label')}
                      necessity="required"
                      status={meta.touched && meta.error ? 'danger' : 'default'}
                      statusMessage={meta.touched && meta.error ? meta.error : undefined}
                    >
                      <TextInput
                        data-testid="add-question-label"
                        placeholder={t('grants.forms.questions.addModal.label.placeholder')}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        status={meta.touched && meta.error ? 'danger' : 'default'}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="key">
                  {({ field, meta }: FieldProps<string>) => (
                    <FormField
                      label={t('grants.forms.questions.addModal.key.label')}
                      necessity="required"
                      status={meta.touched && meta.error ? 'danger' : 'default'}
                      statusMessage={meta.touched && meta.error ? meta.error : undefined}
                    >
                      <TextInput
                        data-testid="add-question-key"
                        placeholder={t('grants.forms.questions.addModal.key.placeholder')}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        status={meta.touched && meta.error ? 'danger' : 'default'}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="displayName">
                  {({ field }: FieldProps<string>) => (
                    <FormField label={t('grants.forms.questions.addModal.displayName.label')} necessity="optional">
                      <TextInput
                        data-testid="add-question-display-name"
                        placeholder={t('grants.forms.questions.addModal.displayName.placeholder')}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="type">
                  {({ field, meta, form }: FieldProps) => (
                    <FormField
                      label={t('grants.forms.questions.addModal.type.label')}
                      necessity="required"
                      status={meta.touched && meta.error ? 'danger' : 'default'}
                      statusMessage={meta.touched && meta.error ? meta.error : undefined}
                    >
                      <Select
                        data-testid="add-question-type"
                        name={field.name}
                        options={questionTypeOptions}
                        defaultValue={questionTypeOptions.find(o => o.value === field.value)}
                        onChange={e => {
                          const newType = e.value as FormQuestionType
                          form.setFieldValue('type', newType)
                          form.setFieldValue('maxScore', '')
                          form.setFieldValue('scoreWeight', newType === 'score' ? 1 : '')
                          form.setFieldValue('maxLength', '')

                          if (newType === 'score') form.setFieldValue('required', true)
                          form.setFieldValue('contactType', '')
                          form.setFieldValue('nameType', '')
                          form.setFieldValue('multiple', false)
                          form.setFieldValue('addOrganisationQuestions', [{ uid: generateUid(), value: '' }])
                          form.setFieldValue('validationRules', [])

                          if (newType === 'decision') {
                            form.setFieldValue('options', DECISION_OPTIONS)
                          } else {
                            form.setFieldValue('options', [
                              { uid: generateUid(), key: '', label: '', conditionalQuestionIds: '' }
                            ])
                          }
                        }}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="placeholder">
                  {({ field }: FieldProps<string>) => (
                    <FormField label={t('grants.forms.questions.addModal.placeholder.label')} necessity="optional">
                      <TextInput
                        data-testid="add-question-placeholder"
                        placeholder={t('grants.forms.questions.addModal.placeholder.placeholder')}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="instructions">
                  {({ field, form }: FieldProps<string>) => (
                    <FormField label={t('grants.forms.questions.addModal.instructions.label')} necessity="optional">
                      <MarkdownTextArea
                        name={field.name}
                        content={field.value}
                        placeholder={t('grants.forms.questions.addModal.instructions.placeholder')}
                        onChange={value => form.setFieldValue('instructions', value)}
                        testId="add-question-instructions"
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="helpText">
                  {({ field }: FieldProps<string>) => (
                    <FormField label={t('grants.forms.questions.addModal.helpText.label')} necessity="optional">
                      <TextInput
                        data-testid="add-question-help-text"
                        placeholder={t('grants.forms.questions.addModal.helpText.placeholder')}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                      />
                    </FormField>
                  )}
                </Field>

                {type === 'textarea' && (
                  <Field name="maxLength">
                    {({ field, meta, form }: FieldProps) => (
                      <FormField
                        label={t('grants.forms.questions.addModal.maxLength.label')}
                        necessity="optional"
                        status={meta.touched && meta.error ? 'danger' : 'default'}
                        statusMessage={meta.touched && meta.error ? meta.error : undefined}
                      >
                        <TextInput
                          data-testid="add-question-max-length"
                          type="number"
                          min={1}
                          placeholder={t('grants.forms.questions.addModal.maxLength.placeholder')}
                          name={field.name}
                          value={field.value}
                          onChange={e => {
                            const val = e.target.valueAsNumber
                            form.setFieldValue('maxLength', Number.isNaN(val) ? '' : Math.floor(val))
                          }}
                          onBlur={field.onBlur}
                        />
                      </FormField>
                    )}
                  </Field>
                )}

                {type === 'score' && (
                  <>
                    <Field name="maxScore">
                      {({ field, meta, form }: FieldProps) => (
                        <FormField
                          label={t('grants.forms.questions.addModal.maxScore.label')}
                          necessity="required"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <TextInput
                            data-testid="add-question-max-score"
                            type="number"
                            min={1}
                            max={10}
                            step={1}
                            placeholder={t('grants.forms.questions.addModal.maxScore.placeholder')}
                            name={field.name}
                            value={field.value}
                            onChange={e => {
                              const val = e.target.valueAsNumber
                              form.setFieldValue('maxScore', Number.isNaN(val) ? '' : Math.floor(val))
                            }}
                            onBlur={field.onBlur}
                            status={meta.touched && meta.error ? 'danger' : 'default'}
                          />
                        </FormField>
                      )}
                    </Field>

                    <Field name="scoreWeight">
                      {({ field, meta, form }: FieldProps) => (
                        <FormField
                          label={t('grants.forms.questions.addModal.scoreWeight.label')}
                          necessity="required"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <TextInput
                            data-testid="add-question-score-weight"
                            type="number"
                            min={0}
                            step="any"
                            placeholder={t('grants.forms.questions.addModal.scoreWeight.placeholder')}
                            name={field.name}
                            value={field.value}
                            onChange={e => {
                              const val = e.target.valueAsNumber
                              form.setFieldValue('scoreWeight', Number.isNaN(val) ? '' : val)
                            }}
                            onBlur={field.onBlur}
                            status={meta.touched && meta.error ? 'danger' : 'default'}
                          />
                        </FormField>
                      )}
                    </Field>
                  </>
                )}

                {type === 'email' && (
                  <Field name="contactType">
                    {({ field, form }: FieldProps) => (
                      <FormField label={t('grants.forms.questions.addModal.contactType.label')} necessity="optional">
                        <Select
                          data-testid="add-question-contact-type"
                          name={field.name}
                          options={contactTypeOptions}
                          defaultValue={contactTypeOptions.find(o => o.value === field.value)}
                          onChange={e => form.setFieldValue('contactType', e.value)}
                          placeholder={t('grants.forms.questions.addModal.contactType.placeholder')}
                        />
                      </FormField>
                    )}
                  </Field>
                )}

                {type === 'name' && (
                  <>
                    <Field name="nameType">
                      {({ field, meta, form }: FieldProps) => (
                        <FormField
                          label={t('grants.forms.questions.addModal.nameType.label')}
                          necessity="required"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <Select
                            data-testid="add-question-name-type"
                            name={field.name}
                            options={nameTypeOptions}
                            defaultValue={nameTypeOptions.find(o => o.value === field.value)}
                            onChange={e => form.setFieldValue('nameType', e.value)}
                            placeholder={t('grants.forms.questions.addModal.nameType.placeholder')}
                          />
                        </FormField>
                      )}
                    </Field>
                    <Field name="contactType">
                      {({ field, meta, form }: FieldProps) => (
                        <FormField
                          label={t('grants.forms.questions.addModal.contactType.label')}
                          necessity="required"
                          status={meta.touched && meta.error ? 'danger' : 'default'}
                          statusMessage={meta.touched && meta.error ? meta.error : undefined}
                        >
                          <Select
                            data-testid="add-question-contact-type"
                            name={field.name}
                            options={contactTypeOptions}
                            defaultValue={contactTypeOptions.find(o => o.value === field.value)}
                            onChange={e => form.setFieldValue('contactType', e.value)}
                            placeholder={t('grants.forms.questions.addModal.contactType.placeholder')}
                          />
                        </FormField>
                      )}
                    </Field>
                  </>
                )}

                {type === 'organisation_search' && (
                  <FieldArray name="addOrganisationQuestions">
                    {({ push, remove }) => (
                      <FormField
                        label={t('grants.forms.questions.addModal.addOrganisationQuestions.label')}
                        necessity="optional"
                      >
                        <Flex direction="column" gap="sm">
                          {formik.values.addOrganisationQuestions.map((orgQ, index) => (
                            <Flex key={orgQ.uid} gap="sm" align="center">
                              <div style={{ flex: 1 }}>
                                <Field name={`addOrganisationQuestions.${index}.value`}>
                                  {({ field }: FieldProps<string>) => (
                                    <TextInput
                                      data-testid={`add-question-org-question-${index}`}
                                      placeholder={t(
                                        'grants.forms.questions.addModal.addOrganisationQuestions.placeholder'
                                      )}
                                      name={field.name}
                                      value={field.value}
                                      onChange={field.onChange}
                                      onBlur={field.onBlur}
                                    />
                                  )}
                                </Field>
                              </div>
                              {formik.values.addOrganisationQuestions.length > 1 && (
                                <Button
                                  variant="reject"
                                  size="small"
                                  onClick={() => remove(index)}
                                  data-testid={`remove-org-question-${index}`}
                                >
                                  {t('button.remove')}
                                </Button>
                              )}
                            </Flex>
                          ))}
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => push({ uid: generateUid(), value: '' })}
                            data-testid="add-org-question-button"
                          >
                            {t('grants.forms.questions.addModal.addOrganisationQuestions.addButton')}
                          </Button>
                        </Flex>
                      </FormField>
                    )}
                  </FieldArray>
                )}

                {hasOptions && (
                  <FieldArray name="options">
                    {({ push, remove }) => (
                      <FormField label={t('grants.forms.questions.addModal.options.label')} necessity="required">
                        <Flex direction="column" gap="sm">
                          {formik.values.options.map((option, index) => (
                            <Flex key={option.uid} gap="sm" align="flex-start">
                              {!isDecision && (
                                <div style={{ flex: 1 }}>
                                  <Field name={`options.${index}.key`}>
                                    {({ field, meta }: FieldProps<string>) => (
                                      <FormField
                                        label={
                                          index === 0 ? t('grants.forms.questions.addModal.options.key') : undefined
                                        }
                                        status={meta.touched && meta.error ? 'danger' : 'default'}
                                        statusMessage={meta.touched && meta.error ? meta.error : undefined}
                                      >
                                        <TextInput
                                          data-testid={`option-key-${index}`}
                                          placeholder={t('grants.forms.questions.addModal.options.keyPlaceholder')}
                                          name={field.name}
                                          value={field.value}
                                          onChange={field.onChange}
                                          onBlur={field.onBlur}
                                          status={meta.touched && meta.error ? 'danger' : 'default'}
                                        />
                                      </FormField>
                                    )}
                                  </Field>
                                </div>
                              )}

                              <div style={{ flex: 1 }}>
                                <Field name={`options.${index}.label`}>
                                  {({ field, meta }: FieldProps<string>) => (
                                    <FormField
                                      label={
                                        isDecision
                                          ? `${option.key} — ${t(
                                              'grants.forms.questions.addModal.options.label_field'
                                            )}`
                                          : index === 0
                                          ? t('grants.forms.questions.addModal.options.label_field')
                                          : undefined
                                      }
                                      status={meta.touched && meta.error ? 'danger' : 'default'}
                                      statusMessage={meta.touched && meta.error ? meta.error : undefined}
                                    >
                                      <TextInput
                                        data-testid={`option-label-${index}`}
                                        placeholder={t('grants.forms.questions.addModal.options.labelPlaceholder')}
                                        name={field.name}
                                        value={field.value}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        status={meta.touched && meta.error ? 'danger' : 'default'}
                                      />
                                    </FormField>
                                  )}
                                </Field>
                              </div>

                              {TYPES_WITH_CONDITIONALS.includes(type) && (
                                <div style={{ flex: 2 }}>
                                  <Field name={`options.${index}.conditionalQuestionIds`}>
                                    {({ field }: FieldProps<string>) => (
                                      <FormField
                                        label={
                                          index === 0
                                            ? t('grants.forms.questions.addModal.options.conditionalQuestionIds')
                                            : undefined
                                        }
                                      >
                                        <TextInput
                                          data-testid={`option-conditional-ids-${index}`}
                                          placeholder={t(
                                            'grants.forms.questions.addModal.options.conditionalQuestionIdsPlaceholder'
                                          )}
                                          name={field.name}
                                          value={field.value}
                                          onChange={field.onChange}
                                          onBlur={field.onBlur}
                                        />
                                      </FormField>
                                    )}
                                  </Field>
                                </div>
                              )}

                              {!isDecision && formik.values.options.length > 1 && (
                                <div style={index === 0 ? { marginTop: 24 } : undefined}>
                                  <Button
                                    variant="reject"
                                    size="small"
                                    onClick={() => remove(index)}
                                    data-testid={`remove-option-${index}`}
                                  >
                                    {t('button.remove')}
                                  </Button>
                                </div>
                              )}
                            </Flex>
                          ))}

                          {!isDecision && (
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() =>
                                push({ uid: generateUid(), key: '', label: '', conditionalQuestionIds: '' })
                              }
                              data-testid="add-option-button"
                            >
                              {t('grants.forms.questions.addModal.options.addButton')}
                            </Button>
                          )}
                        </Flex>
                      </FormField>
                    )}
                  </FieldArray>
                )}

                {type === 'select' && (
                  <Field name="multiple">
                    {({ field, form }: FieldProps<boolean>) => (
                      <FormField
                        label={t('grants.forms.questions.addModal.multiple.label')}
                        necessity="optional"
                        data-testid="add-question-multiple-field"
                      >
                        <Select
                          name="multiple"
                          options={[
                            { label: t('button.yes'), value: String(true) },
                            { label: t('button.no'), value: String(false) }
                          ]}
                          defaultValue={{
                            label: field.value ? t('button.yes') : t('button.no'),
                            value: String(field.value)
                          }}
                          onChange={option => form.setFieldValue('multiple', option.value === String(true))}
                          data-testid="add-question-multiple-select"
                        />
                      </FormField>
                    )}
                  </Field>
                )}

                {TYPES_WITH_VALIDATION_RULES.includes(type) && (
                  <ValidationRulesSection validationRules={formik.values.validationRules} testIdPrefix="add" />
                )}

                <Field name="partnerId">
                  {({ form }: FieldProps) => (
                    <FormField label={t('grants.forms.questions.addModal.partner.label')} necessity="optional">
                      <AsyncSelect
                        data-testid="add-question-partner"
                        name="partnerId"
                        options={partnerNameOptions}
                        setQuery={setPartnerSearch}
                        query={partnerSearch}
                        loading={isPartnersLoading}
                        onChange={selected => form.setFieldValue('partnerId', selected?.value ?? '')}
                        placeholder={t('grants.forms.questions.addModal.partner.genericOption')}
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="required">
                  {({ field, form }: FieldProps<boolean>) => {
                    // Score questions are always required, so the toggle is forced on and disabled.
                    const isScore = form.values.type === 'score'
                    const value = isScore || field.value

                    return (
                      <FormField
                        label={t('grants.forms.questions.addModal.required.label')}
                        necessity="required"
                        data-testid="add-question-required-field"
                      >
                        <Select
                          key={isScore ? 'required-locked' : 'required-editable'}
                          name="required"
                          disabled={isScore}
                          options={[
                            { label: t('button.yes'), value: String(true) },
                            { label: t('button.no'), value: String(false) }
                          ]}
                          defaultValue={{
                            label: value ? t('button.yes') : t('button.no'),
                            value: String(value)
                          }}
                          onChange={option => form.setFieldValue('required', option.value === String(true))}
                          data-testid="add-question-required-select"
                        />
                      </FormField>
                    )
                  }}
                </Field>

                <Field name="hidden">
                  {({ field, form }: FieldProps<boolean>) => (
                    <FormField
                      label={t('grants.forms.questions.addModal.hidden.label')}
                      necessity="optional"
                      data-testid="add-question-hidden-field"
                    >
                      <Select
                        name="hidden"
                        options={[
                          { label: t('button.yes'), value: String(true) },
                          { label: t('button.no'), value: String(false) }
                        ]}
                        defaultValue={{
                          label: field.value ? t('button.yes') : t('button.no'),
                          value: String(field.value)
                        }}
                        onChange={option => form.setFieldValue('hidden', option.value === String(true))}
                        data-testid="add-question-hidden-select"
                      />
                    </FormField>
                  )}
                </Field>

                <Field name="autoTranslationsEnabled">
                  {({ field, form }: FieldProps<boolean>) => (
                    <FormField
                      label={t('grants.forms.questions.autoTranslationsEnabled.label')}
                      necessity="optional"
                      data-testid="add-question-auto-translations-enabled-field"
                    >
                      <Select
                        name="autoTranslationsEnabled"
                        options={[
                          { label: t('button.yes'), value: String(true) },
                          { label: t('button.no'), value: String(false) }
                        ]}
                        defaultValue={{
                          label: field.value ? t('button.yes') : t('button.no'),
                          value: String(field.value)
                        }}
                        onChange={option =>
                          form.setFieldValue('autoTranslationsEnabled', option.value === String(true))
                        }
                        data-testid="add-question-auto-translations-enabled-select"
                      />
                    </FormField>
                  )}
                </Field>
              </Flex>
            </ActionModal>
          )
        }}
      </Formik>
    )
  }

  return (
    <Modal open={isOpen} onClose={handleClose} aria-labelledby="add-question-modal" showModalOverflow={false}>
      {renderContent()}
    </Modal>
  )
}
